import React, { useState, useEffect, useRef, useContext } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import Typography from "@material-ui/core/Typography";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Badge from "@material-ui/core/Badge";
import Chip from "@material-ui/core/Chip";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	ticket: {
		position: "relative",
		margin: "4px 8px",
		width: "auto",
		borderRadius: 14,
		padding: "10px 10px 10px 14px",
		minHeight: 78,
		alignItems: "flex-start",
		transition: "background-color 160ms ease, transform 160ms ease",
		"&:hover": {
			transform: "translateY(-1px)",
		},
		"&.Mui-selected": {
			backgroundColor: theme.palette.action.selected,
		},
	},

	pendingTicket: {
		cursor: "unset",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	contactNameWrapper: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		justifyContent: "space-between",
	},

	lastMessageTime: {
		justifySelf: "flex-end",
	},

	closedBadge: {
		alignSelf: "center",
		justifySelf: "flex-end",
		marginRight: 32,
		marginLeft: "auto",
	},

	contactLastMessage: {
		minWidth: 0,
		flex: 1,
	},

	newMessagesCount: {
		alignSelf: "center",
		marginRight: 8,
		marginLeft: "auto",
	},

	badgeStyle: {
		color: "white",
		backgroundColor: green[500],
	},

	acceptButton: {
		marginLeft: 8,
		borderRadius: 18,
		textTransform: "none",
	},

	ticketQueueColor: {
		flex: "none",
		width: "4px",
		height: "calc(100% - 18px)",
		position: "absolute",
		top: 9,
		left: 4,
		borderRadius: 6,
	},

	content: {
		minWidth: 0,
		flex: 1,
	},
	avatar: {
		width: 44,
		height: 44,
	},
	messageRow: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		marginTop: 3,
	},
	chips: {
		display: "flex",
		alignItems: "center",
		gap: 5,
		marginTop: 7,
		overflow: "hidden",
	},
	chip: {
		height: 20,
		maxWidth: 115,
		fontSize: 10,
		borderRadius: 8,
	},
	originChip: {
		height: 20,
		fontSize: 10,
		borderRadius: 8,
		backgroundColor: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
	},
}));

const TicketListItem = ({ ticket }) => {
	const classes = useStyles();
	const history = useHistory();
	const [loading, setLoading] = useState(false);
	const { ticketId } = useParams();
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleAcepptTicket = async id => {
		setLoading(true);
		try {
			await api.put(`/tickets/${id}`, {
				status: "open",
				userId: user?.id,
			});
		} catch (err) {
			setLoading(false);
			toastError(err);
		}
		if (isMounted.current) {
			setLoading(false);
		}
		history.push(`/tickets/${id}`);
	};

	const handleSelectTicket = id => {
		history.push(`/tickets/${id}`);
	};

	return (
		<ListItem
			button
			onClick={() => {
				if (ticket.status !== "pending") handleSelectTicket(ticket.id);
			}}
			selected={ticketId && +ticketId === ticket.id}
			className={clsx(classes.ticket, {
				[classes.pendingTicket]: ticket.status === "pending",
			})}
		>
			<Tooltip arrow placement="right" title={ticket.queue?.name || "Sem fila"}>
				<span
					style={{ backgroundColor: ticket.queue?.color || "#7C7C7C" }}
					className={classes.ticketQueueColor}
				/>
			</Tooltip>
			<ListItemAvatar>
				<Avatar className={classes.avatar} src={ticket?.contact?.profilePicUrl}>
					{ticket.contact.name?.charAt(0)}
				</Avatar>
			</ListItemAvatar>
			<div className={classes.content}>
				<div className={classes.contactNameWrapper}>
					<Typography noWrap variant="body2">
						<strong>{ticket.contact.name}</strong>
					</Typography>
					<Typography className={classes.lastMessageTime} variant="caption" color="textSecondary">
						{isSameDay(parseISO(ticket.updatedAt), new Date())
							? format(parseISO(ticket.updatedAt), "HH:mm")
							: format(parseISO(ticket.updatedAt), "dd/MM")}
					</Typography>
				</div>
				<div className={classes.messageRow}>
					<Typography
						className={classes.contactLastMessage}
						noWrap
						component="span"
						variant="body2"
						color="textSecondary"
					>
						{ticket.lastMessage ? (
							<MarkdownWrapper>{ticket.lastMessage}</MarkdownWrapper>
						) : (
							"Sem mensagens"
						)}
					</Typography>
					<Badge
						className={classes.newMessagesCount}
						badgeContent={ticket.unreadMessages}
						classes={{ badge: classes.badgeStyle }}
					/>
					{ticket.status === "pending" && (
						<ButtonWithSpinner
							color="primary"
							variant="contained"
							className={classes.acceptButton}
							size="small"
							loading={loading}
							onClick={event => {
								event.stopPropagation();
								handleAcepptTicket(ticket.id);
							}}
						>
							{i18n.t("ticketsList.buttons.accept")}
						</ButtonWithSpinner>
					)}
				</div>
				<div className={classes.chips}>
					<Chip
						className={classes.chip}
						size="small"
						label={ticket.queue?.name || "Sem fila"}
						variant="outlined"
					/>
					{ticket.user?.name && (
						<Chip
							className={classes.chip}
							size="small"
							label={ticket.user.name}
							variant="outlined"
						/>
					)}
					{ticket.whatsappId && (
						<Chip
							className={classes.originChip}
							size="small"
							label={ticket.whatsapp?.name || "WhatsApp"}
							title={i18n.t("ticketsList.connectionTitle")}
						/>
					)}
				</div>
			</div>
		</ListItem>
	);
};

export default TicketListItem;
